import type {
  PictureSource,
  PictureStyleFamily,
  PictureStyleRead,
  PictureTreatment,
  PictureVariety,
} from '../../types/api';

/**
 * Words for the picture-variety vocabulary (IMAGE_VARIETY_PLAN §12).
 *
 * The style list itself comes from GET /picture-styles; the family headings,
 * the shot words and the `custom` label are the SPA's own, like every label.
 */

/** The resolved style the design agent names when it invents its own medium. */
export const CUSTOM_PICTURE_STYLE = 'custom';

/** Family headings, in the order the registry groups them. */
export const PICTURE_STYLE_FAMILIES: readonly { key: PictureStyleFamily; label: string }[] = [
  { key: 'photo', label: 'Photo' },
  { key: 'illustration', label: 'Illustration' },
  { key: 'dimensional', label: '3D' },
];

/** The styles grouped by family, registry order kept inside each; empty families dropped. */
export function groupPictureStyles(
  styles: readonly PictureStyleRead[],
): { key: PictureStyleFamily; label: string; styles: PictureStyleRead[] }[] {
  return PICTURE_STYLE_FAMILIES.map((family) => ({
    ...family,
    styles: styles.filter((style) => style.family === family.key),
  })).filter((family) => family.styles.length > 0);
}

/** Narrows an untrusted value (draft, prefill) to a key the registry knows, else null. */
export function readPictureStyle(
  value: unknown,
  styles: readonly PictureStyleRead[],
): string | null {
  if (typeof value !== 'string') return null;
  return styles.some((style) => style.key === value) ? value : null;
}

/** Narrows an untrusted value to a deck variety, else null (consistent). */
export function readPictureVariety(value: unknown): PictureVariety | null {
  return value === 'consistent' || value === 'varied' ? value : null;
}

/** Readable words for a key the SPA has no label for: `flat_lay` → "Flat lay", `chart-bar` → "Chart bar". */
function humanise(key: string): string {
  const words = key.replace(/[_-]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The label for a picture style key: the registry's own, "Custom" for the
 * agent's own medium, and the key in words for one this build does not know.
 */
export function pictureStyleLabel(key: string, styles: readonly PictureStyleRead[]): string {
  if (key === CUSTOM_PICTURE_STYLE) return 'Custom';
  return styles.find((style) => style.key === key)?.label ?? humanise(key);
}

/** Shot words, for the closed shot vocabulary. */
const SHOT_LABELS: Readonly<Record<string, string>> = {
  macro_detail: 'Macro detail',
  flat_lay: 'Flat lay',
  wide_establishing: 'Wide shot',
  environmental_portrait: 'Portrait in place',
  over_shoulder_pov: 'Over the shoulder',
  hero_object: 'Hero object',
  still_life_group: 'Still life',
  motion: 'Motion',
  aerial: 'Aerial',
  split_before_after: 'Before and after',
  pattern_repeat: 'Pattern',
  silhouette: 'Silhouette',
  abstract_texture: 'Abstract texture',
  visual_metaphor: 'Visual metaphor',
};

/** A shot in words; an unknown key reads as its own words rather than vanishing. */
export function shotLabel(shot: string): string {
  return SHOT_LABELS[shot] ?? humanise(shot);
}

/**
 * Words for the seven picture treatments (IMAGE_VARIETY_PLAN §13 T9). A
 * `Record` so a new arm of the union is a compile error until it has a label.
 * `masked` reads "Shaped": the user sees the shape, not the mask.
 */
export const PICTURE_TREATMENT_LABELS: Readonly<Record<PictureTreatment, string>> = {
  full_bleed: 'Full bleed',
  framed: 'Framed',
  masked: 'Shaped',
  cutout: 'Cut-out',
  grain: 'Grain',
  halftone: 'Halftone',
  tritone: 'Tritone',
};

/** Picker order, which is the backend's `PICTURE_TREATMENTS` order. */
export const PICTURE_TREATMENT_ORDER: readonly PictureTreatment[] = [
  'full_bleed',
  'framed',
  'masked',
  'cutout',
  'grain',
  'halftone',
  'tritone',
];

/** The renderer's default, and what a missing or unknown value reads as. */
export const DEFAULT_PICTURE_TREATMENT: PictureTreatment = 'full_bleed';

/** Narrows an untrusted value to a treatment, or null when it is not one. */
export function isPictureTreatment(value: unknown): value is PictureTreatment {
  return typeof value === 'string' && Object.hasOwn(PICTURE_TREATMENT_LABELS, value);
}

/**
 * A slide's treatment read tolerantly, like the backend's `known_treatment`:
 * a server that predates treatments omits the field, and an unknown value
 * renders as the default rather than failing.
 */
export function readPictureTreatment(value: unknown): PictureTreatment {
  return isPictureTreatment(value) ? value : DEFAULT_PICTURE_TREATMENT;
}

/** An icon's Lucide name in words: `chart-bar` → "Chart bar". */
export function iconLabel(name: string): string {
  return humanise(name);
}

/**
 * The per-slide tag, by where the picture came from (IMAGE_VARIETY_PLAN §14
 * S6): an icon slide reads "Icon · {name}", a type-only slide "Type only",
 * the brand's own photo "Your photo". A generated picture, and a slide
 * written before sources existed (`picture_source` null or missing), keeps
 * "{style} · {shot}", plus " · {treatment}" when the picture is not full
 * bleed, or null when the slide carries none of them (pack, community and
 * older slides).
 */
export function slidePictureTag(
  slide: {
    picture_style: string | null;
    shot: string | null;
    picture_treatment?: PictureTreatment | null;
    picture_source?: PictureSource | null;
    icon?: string | null;
  },
  styles: readonly PictureStyleRead[],
): string | null {
  switch (slide.picture_source) {
    case 'icon':
      return slide.icon ? `Icon · ${iconLabel(slide.icon)}` : 'Icon';
    case 'none':
      return 'Type only';
    case 'brand_asset':
      return 'Your photo';
    default:
      break;
  }
  const parts: string[] = [];
  if (slide.picture_style) parts.push(pictureStyleLabel(slide.picture_style, styles));
  if (slide.shot) parts.push(shotLabel(slide.shot));
  const treatment = readPictureTreatment(slide.picture_treatment);
  if (treatment !== DEFAULT_PICTURE_TREATMENT) parts.push(PICTURE_TREATMENT_LABELS[treatment]);
  return parts.length > 0 ? parts.join(' · ') : null;
}
